import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bell, ArrowLeft, RefreshCw, Settings as SettingsIcon } from "lucide-react";
import api from "@/lib/api";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtRel(iso) {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const STATUS_TONE = {
  active: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]",
  alert:  "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]",
  idle:   "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]",
};

/**
 * NotificationsPage — Full "See all" view for the notification bell. Reads the
 * same `/notifications/agent_runs` endpoint (no cursor for now — showing the last
 * 100 events is enough for Phase 0). Watchlist status shown as a heads-up chip
 * so operators immediately see whether they're missing agents.
 */
export default function NotificationsPage() {
  const nav = useNavigate();
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [watchlist, setWatchlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/notifications/agent_runs?limit=100");
      setItems(data.items || []);
      setWatchlist(data.watchlist);
      // On visit: mark all as read.
      await api.post("/notifications/mark-read", {}).catch(() => {});
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const backToSettings = () => nav(slug ? `/${slug}/app/settings` : "/admin/tenants");
  const backToOverview = () => nav(slug ? `/${slug}/app/overview` : "/admin/tenants");

  const watchlistLabel = watchlist === null
    ? "Watching all agents"
    : watchlist.length === 0
    ? "Watching no agents"
    : `Watching ${watchlist.length} agent${watchlist.length === 1 ? "" : "s"}`;

  return (
    <div data-testid="notifications-page" className="space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={backToOverview}
                  data-testid="notif-back"
                  className="text-[12px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420] inline-flex items-center gap-1 mb-2">
            <ArrowLeft size={12} strokeWidth={1.8} /> Back to Overview
          </button>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] inline-flex items-center gap-2.5">
            <Bell size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Notifications
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Latest agent runs across your tenant. Marks as read on open.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="notif-refresh" disabled={refreshing}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8] disabled:opacity-50">
            <RefreshCw size={13} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <button onClick={backToSettings} data-testid="notif-settings-link"
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8]">
            <SettingsIcon size={13} strokeWidth={1.8} />
            Manage watchlist
          </button>
        </div>
      </header>

      <div data-testid="notif-watchlist-status"
           className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-widest font-semibold text-[#1c6a99] bg-[#e7f4fb] border border-[#c5e0ee] px-3 py-1.5 rounded-md">
        <span>{watchlistLabel}</span>
      </div>

      <section className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[13.5px] text-[color:var(--cc-ink-muted)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-[13.5px] text-[color:var(--cc-ink-muted)]">
            No agent runs yet {watchlist && watchlist.length === 0 ? "(your watchlist is empty — visit Settings to add agents)" : ""}.
          </div>
        ) : (
          <ul className="divide-y divide-[#eef1f6]">
            {items.map((it) => (
              <li key={it.id} className="px-4 sm:px-5 py-3.5" data-testid={`notif-row-${it.id}`}>
                <div className="flex items-start gap-3">
                  <div
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold"
                    style={{ background: it.avatar_color || "#4c8dff" }}
                    aria-hidden
                  >
                    {(it.initial || (it.agent_name || "?").slice(0, 1)).slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[13.5px] font-semibold text-[#0e1420]">{it.agent_name || it.agent_id}</div>
                      <span className={`text-[10px] font-mono-data uppercase tracking-widest border rounded px-1.5 py-0.5 font-bold ${STATUS_TONE[it.status] || STATUS_TONE.idle}`}>
                        {it.status}
                      </span>
                    </div>
                    {it.summary && (
                      <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1">{it.summary}</div>
                    )}
                    {it.error && (
                      <div className="text-[12px] text-[#c73535] mt-1 font-mono-data">{it.error}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] font-mono-data">{fmtRel(it.started_at)}</div>
                    <div className="text-[10.5px] text-[#0e1420] font-mono-data mt-0.5">{fmtDateTime(it.started_at)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
